import { ref, computed } from 'vue';
import { useUsuariosStore } from '@/stores/usuarios';
import { useTurnosStore } from '@/stores/turnos';

const CLIENT_ID = process.env.VUE_APP_GOOGLE_CLIENT_ID;
const SCOPES = 'https://www.googleapis.com/auth/calendar.events';
const CALENDAR_AUTORIZADO_KEY = 'gcal_autorizado';

// Estado singleton compartido entre todas las instancias
const gapiIniciado = ref(false);
const tokenClient = ref(null);
const conectado = ref(false);
const cargando = ref(false);
const error = ref(null);
const eventosPendientes = ref([]);

export function useGoogleCalendar() {
  const usuariosStore = useUsuariosStore();
  const turnosStore = useTurnosStore();

  const calendarHabilitado = computed(() => {
    return usuariosStore.usuarioActual?.googleCalendarHabilitado === true;
  });

  /**
   * Cargar la librería de Google API e inicializar el cliente de Calendar.
   * Siempre verifica que gapi.client.calendar esté disponible antes de resolver.
   */
  const DISCOVERY_DOC = 'https://www.googleapis.com/discovery/v1/apis/calendar/v3/rest';

  const cargarGoogleAPI = () => {
    return new Promise((resolve, reject) => {
      // Paso 2: gapi.client ya cargado → cargar el discovery doc de Calendar
      const inicializarCliente = () => {
        window.gapi.client
          .load(DISCOVERY_DOC)
          .then(() => {
            gapiIniciado.value = true;
            resolve();
          })
          .catch(reject);
      };

      // Paso 1: gapi cargado → cargar el módulo 'client' y luego el discovery doc
      const cargarModuloClient = () => {
        window.gapi.load('client', inicializarCliente);
      };

      // Caso A: todo ya listo
      if (window.gapi?.client?.calendar) {
        resolve();
        return;
      }

      // Caso B: gapi.client ya existe pero calendar no → solo cargar discovery doc
      if (window.gapi?.client) {
        inicializarCliente();
        return;
      }

      // Caso C: gapi existe pero gapi.client no → cargar módulo client primero
      if (window.gapi) {
        cargarModuloClient();
        return;
      }

      // Caso D: primera carga, agregar el script de gapi
      const script = document.createElement('script');
      script.src = 'https://apis.google.com/js/api.js';
      script.onload = cargarModuloClient;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  /**
   * Cargar Google Identity Services (para OAuth)
   */
  const cargarGIS = () => {
    return new Promise((resolve, reject) => {
      if (window.google?.accounts?.oauth2) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.onload = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  };

  /**
   * Inicializar cliente de autenticación
   */
  const inicializarTokenClient = () => {
    tokenClient.value = window.google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID,
      scope: SCOPES,
      callback: '', // Se define en cada solicitud
    });
  };

  /**
   * Conectar con Google Calendar
   */
  const conectarGoogleCalendar = async () => {
    if (!calendarHabilitado.value) {
      error.value = 'Google Calendar no habilitado para este usuario';
      return { exito: false, mensaje: error.value };
    }

    try {
      cargando.value = true;
      error.value = null;

      await cargarGoogleAPI();
      await cargarGIS();

      if (!tokenClient.value) {
        inicializarTokenClient();
      }

      return new Promise((resolve) => {
        const state = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2);
        sessionStorage.setItem('oauth_state', state);

        tokenClient.value.callback = async (response) => {
          const savedState = sessionStorage.getItem('oauth_state');
          sessionStorage.removeItem('oauth_state');

          if (response.error) {
            error.value = response.error;
            conectado.value = false;
            cargando.value = false;
            resolve({ exito: false, mensaje: response.error });
            return;
          }

          if (response.state !== savedState) {
            error.value = 'Error de seguridad en la autenticación';
            conectado.value = false;
            cargando.value = false;
            resolve({ exito: false, mensaje: 'Error de seguridad en la autenticación' });
            return;
          }

          conectado.value = true;
          cargando.value = false;
          localStorage.setItem(CALENDAR_AUTORIZADO_KEY, '1');
          resolve({ exito: true, mensaje: 'Conectado a Google Calendar' });
        };

        tokenClient.value.requestAccessToken({ prompt: 'consent', state });
      });
    } catch (err) {
      error.value = err.message;
      cargando.value = false;
      return { exito: false, mensaje: err.message };
    }
  };

  /**
   * Desconectar de Google Calendar
   */
  const desconectarGoogleCalendar = () => {
    try {
      const token = window.gapi?.client?.getToken();
      if (token) {
        window.google?.accounts?.oauth2?.revoke(token.access_token);
        window.gapi.client.setToken('');
      }
    } catch (err) {
      console.warn('Error al revocar token de Google:', err);
    }
    // Siempre limpiar el estado local, independientemente de errores
    conectado.value = false;
    localStorage.removeItem(CALENDAR_AUTORIZADO_KEY);
    return { exito: true, mensaje: 'Desconectado de Google Calendar' };
  };

  /**
   * Precargar los scripts de Google en background.
   * Debe llamarse en onMounted para que cuando el usuario toque "Conectar",
   * los scripts ya estén listos y el popup se abra sin demora.
   * En mobile, los navegadores bloquean popups que no abren en el mismo tick del gesto.
   */
  const reconectarSilencioso = async () => {
    try {
      // Siempre precargar scripts, sin importar si el usuario autorizó antes.
      await cargarGoogleAPI();
      await cargarGIS();
      if (!tokenClient.value) {
        inicializarTokenClient();
      }
    } catch (err) {
      return { exito: false, mensaje: err.message };
    }

    // Si no hay autorización previa, solo precargar scripts y salir.
    if (!localStorage.getItem(CALENDAR_AUTORIZADO_KEY)) {
      return { exito: false, mensaje: 'Sin autorización previa' };
    }

    // Intentar reconexión silenciosa (sin popup)
    return new Promise((resolve) => {
      const state = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2);
      sessionStorage.setItem('oauth_state_silent', state);

      tokenClient.value.callback = (response) => {
        const savedState = sessionStorage.getItem('oauth_state_silent');
        sessionStorage.removeItem('oauth_state_silent');

        if (response.error || response.state !== savedState) {
          conectado.value = false;
          resolve({ exito: false, mensaje: response.error || 'Error de seguridad' });
          return;
        }
        conectado.value = true;
        resolve({ exito: true, mensaje: 'Reconectado automáticamente' });
      };
      tokenClient.value.requestAccessToken({ prompt: '', state });
    });
  };

  /**
   * Exportar un turno a Google Calendar.
   * Guarda el googleEventId en el turno para evitar duplicados futuros.
   */
  const exportarTurnoACalendar = async (turno) => {
    if (!conectado.value) {
      return { exito: false, mensaje: 'No estás conectado a Google Calendar' };
    }

    // Si ya tiene googleEventId, significa que ya fue exportado → actualizar en lugar de crear
    if (turno.googleEventId) {
      return actualizarEventoEnCalendar(turno);
    }

    try {
      const zonaHoraria = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Usar formato local directo (sin conversión a UTC) para evitar desfase de fecha
      // Ejemplo: "2026-02-17T08:00:00" con timeZone "America/Montevideo"
      const evento = {
        summary: `${turno.paciente} - ${turno.motivo || 'Consulta'}`,
        description: `Paciente: ${turno.paciente}\nMotivo: ${turno.motivo || 'Consulta'}\nEstado: ${turno.estado || 'Pendiente'}`,
        start: {
          dateTime: `${turno.fecha}T${turno.inicio}:00`,
          timeZone: zonaHoraria,
        },
        end: {
          dateTime: `${turno.fecha}T${turno.fin}:00`,
          timeZone: zonaHoraria,
        },
        colorId: turno.estado === 'confirmado' ? '2' : '1',
      };

      const response = await window.gapi.client.calendar.events.insert({
        calendarId: 'primary',
        resource: evento,
      });

      const googleEventId = response.result.id;

      // Guardar el googleEventId en el turno para evitar duplicados en exportaciones futuras
      turnosStore.actualizarTurno(turno.id, { googleEventId });

      return {
        exito: true,
        mensaje: 'Turno exportado a Google Calendar',
        eventoId: googleEventId,
      };
    } catch (err) {
      console.error('Error al exportar turno:', err);
      return { exito: false, mensaje: err.message };
    }
  };

  /**
   * Actualizar un evento existente en Google Calendar.
   * Solo actúa si el turno tiene googleEventId.
   */
  const actualizarEventoEnCalendar = async (turno) => {
    if (!conectado.value) {
      return { exito: false, mensaje: 'No estás conectado a Google Calendar' };
    }

    // Solo actualizar si el turno ya fue exportado previamente
    if (!turno.googleEventId) {
      return { exito: false, mensaje: 'Este turno no tiene un evento de Google Calendar asociado' };
    }

    try {
      const zonaHoraria = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Usar formato local directo (sin conversión a UTC) para evitar desfase de fecha
      const evento = {
        summary: `${turno.paciente} - ${turno.motivo || 'Consulta'}`,
        description: `Paciente: ${turno.paciente}\nMotivo: ${turno.motivo || 'Consulta'}\nEstado: ${turno.estado || 'Pendiente'}`,
        start: {
          dateTime: `${turno.fecha}T${turno.inicio}:00`,
          timeZone: zonaHoraria,
        },
        end: {
          dateTime: `${turno.fecha}T${turno.fin}:00`,
          timeZone: zonaHoraria,
        },
        colorId: turno.estado === 'confirmado' ? '2' : '1',
      };

      await window.gapi.client.calendar.events.update({
        calendarId: 'primary',
        eventId: turno.googleEventId,
        resource: evento,
      });

      return { exito: true, mensaje: 'Evento actualizado en Google Calendar' };
    } catch (err) {
      console.error('Error al actualizar evento en Calendar:', err);
      return { exito: false, mensaje: err.message };
    }
  };

  /**
   * Exportar todos los turnos a Google Calendar.
   * Omite los que ya tienen googleEventId (ya exportados).
   */
  const exportarTodosLosTurnos = async () => {
    if (!conectado.value) {
      return { exito: false, mensaje: 'No estás conectado a Google Calendar' };
    }

    try {
      cargando.value = true;
      let exitosos = 0;
      let omitidos = 0;
      let fallidos = 0;

      const ESTADOS_NO_EXPORTAR = ['eliminada', 'cancelada'];

      for (const turno of turnosStore.turnos) {
        // Omitir turnos eliminados o cancelados
        if (ESTADOS_NO_EXPORTAR.includes(turno.estado)) continue;

        // Omitir turnos ya exportados para evitar duplicados
        if (turno.googleEventId) {
          omitidos++;
          continue;
        }

        const resultado = await exportarTurnoACalendar(turno);
        if (resultado.exito) {
          exitosos++;
        } else {
          fallidos++;
        }
      }

      cargando.value = false;
      return {
        exito: true,
        mensaje: `Exportados: ${exitosos} nuevos. Omitidos (ya exportados): ${omitidos}. Fallidos: ${fallidos}`,
      };
    } catch (err) {
      cargando.value = false;
      return { exito: false, mensaje: err.message };
    }
  };

  /**
   * Sincronizar eventos desde Google Calendar.
   * NO crea pacientes automáticamente.
   * Devuelve los eventos pendientes de confirmación en `eventosPendientes`.
   * La UI debe mostrar un modal de confirmación por cada evento.
   */
  const sincronizarDesdeCalendar = async () => {
    if (!conectado.value) {
      return { exito: false, mensaje: 'No estás conectado a Google Calendar' };
    }

    try {
      cargando.value = true;
      error.value = null;
      eventosPendientes.value = [];

      // Recargar turnos desde Firestore para asegurar que los googleEventId
      // importados desde otro dispositivo estén actualizados antes de comparar.
      await turnosStore.cargarDesdeLocalStorage();

      // Obtener eventos desde el inicio del día de hoy hasta 30 días adelante
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0);
      const en30Dias = new Date();
      en30Dias.setDate(en30Dias.getDate() + 30);

      const response = await window.gapi.client.calendar.events.list({
        calendarId: 'primary',
        timeMin: hoy.toISOString(),
        timeMax: en30Dias.toISOString(),
        singleEvents: true,
        orderBy: 'startTime',
      });

      const eventos = response.result.items || [];

      // Mapa de eventos ya importados: googleEventId → turno
      const turnosPorEventoId = {};
      for (const t of turnosStore.turnos) {
        if (t.googleEventId) turnosPorEventoId[t.googleEventId] = t;
      }

      const pendientes = [];

      for (const evento of eventos) {
        // Si ya fue importado, verificar si cambió el horario
        if (turnosPorEventoId[evento.id]) {
          const turnoExistente = turnosPorEventoId[evento.id];
          const fechaInicio = new Date(evento.start.dateTime);
          const fechaFin = new Date(evento.end?.dateTime || evento.start.dateTime);

          const anio = fechaInicio.getFullYear();
          const mes = String(fechaInicio.getMonth() + 1).padStart(2, '0');
          const dia = String(fechaInicio.getDate()).padStart(2, '0');
          const nuevaFecha = `${anio}-${mes}-${dia}`;
          const nuevoInicio = `${String(fechaInicio.getHours()).padStart(2, '0')}:${String(fechaInicio.getMinutes()).padStart(2, '0')}`;
          const nuevoFin = `${String(fechaFin.getHours()).padStart(2, '0')}:${String(fechaFin.getMinutes()).padStart(2, '0')}`;

          const cambioHorario =
            turnoExistente.fecha !== nuevaFecha ||
            turnoExistente.inicio !== nuevoInicio ||
            turnoExistente.fin !== nuevoFin;

          if (cambioHorario) {
            turnosStore.actualizarTurno(turnoExistente.id, {
              fecha: nuevaFecha,
              inicio: nuevoInicio,
              fin: nuevoFin,
            });
          }
          continue;
        }

        // Saltar eventos sin fecha/hora definida (eventos de día completo sin hora)
        if (!evento.start?.dateTime) continue;

        // Validar datos mínimos necesarios para crear un turno
        if (!evento.summary || evento.summary.trim() === '') continue;

        // Construir la vista previa del turno a importar
        const fechaInicio = new Date(evento.start.dateTime);
        const fechaFin = new Date(evento.end?.dateTime || evento.start.dateTime);

        // Usar getters locales para evitar desfase UTC (ej: 22:00 UY → 01:00Z día siguiente)
        const anio = fechaInicio.getFullYear();
        const mes = String(fechaInicio.getMonth() + 1).padStart(2, '0');
        const dia = String(fechaInicio.getDate()).padStart(2, '0');

        pendientes.push({
          googleEventId: evento.id,
          titulo: evento.summary,
          descripcion: evento.description || '',
          fecha: `${anio}-${mes}-${dia}`,
          inicio: `${String(fechaInicio.getHours()).padStart(2, '0')}:${String(fechaInicio.getMinutes()).padStart(2, '0')}`,
          fin: `${String(fechaFin.getHours()).padStart(2, '0')}:${String(fechaFin.getMinutes()).padStart(2, '0')}`,
          // Sugerencia de nombre de paciente extraída del título (antes del " - " si existe)
          pacienteSugerido: evento.summary.includes(' - ')
            ? evento.summary.split(' - ')[0].trim()
            : evento.summary.trim(),
          motivoSugerido: evento.summary.includes(' - ')
            ? evento.summary.split(' - ').slice(1).join(' - ').trim()
            : '',
        });
      }

      eventosPendientes.value = pendientes;
      cargando.value = false;

      return {
        exito: true,
        pendientes: pendientes.length,
        mensaje: pendientes.length > 0
          ? `Se encontraron ${pendientes.length} evento(s) para importar`
          : 'No hay eventos nuevos para importar',
      };
    } catch (err) {
      cargando.value = false;
      error.value = err.message;
      return { exito: false, mensaje: err.message };
    }
  };

  /**
   * Confirmar la importación de un evento pendiente como turno.
   * Requiere que el pacienteId ya exista: NO crea pacientes automáticamente.
   * @param {Object} evento - Evento de eventosPendientes
   * @param {number} pacienteId - ID del paciente ya existente en la app
   * @param {string} nombrePaciente - Nombre del paciente
   * @param {string} motivo - Motivo de la consulta
   */
  const confirmarImportacion = (evento, pacienteId, nombrePaciente, motivo) => {
    // Validar solapamiento de horarios (misma lógica que NuevoTurno.vue)
    const solapamiento = turnosStore.turnos.find((t) => {
      return t.fecha === evento.fecha && t.inicio && t.fin &&
             t.estado !== 'eliminada' &&
             evento.inicio < t.fin && evento.fin > t.inicio;
    });

    if (solapamiento) {
      return {
        exito: false,
        mensaje: `Ya existe un turno de ${solapamiento.paciente} en ese horario (${solapamiento.inicio}–${solapamiento.fin}). No se puede importar.`,
      };
    }

    const nuevoTurno = {
      id: Date.now(),
      paciente: nombrePaciente,
      pacienteId: pacienteId,
      fecha: evento.fecha,
      inicio: evento.inicio,
      fin: evento.fin,
      motivo: motivo || evento.motivoSugerido || 'Consulta',
      estado: 'pendiente',
      googleEventId: evento.googleEventId, // Vincular para evitar re-importar
    };

    turnosStore.agregarTurno(nuevoTurno);

    // Remover de la lista de pendientes
    eventosPendientes.value = eventosPendientes.value.filter(
      e => e.googleEventId !== evento.googleEventId
    );

    return { exito: true, mensaje: 'Evento importado como turno' };
  };

  /**
   * Rechazar la importación de un evento pendiente (descartarlo).
   */
  const rechazarImportacion = (googleEventId) => {
    eventosPendientes.value = eventosPendientes.value.filter(
      e => e.googleEventId !== googleEventId
    );
  };

  /**
   * Verificar si hay token activo
   */
  const verificarConexion = () => {
    const token = window.gapi?.client?.getToken();
    conectado.value = !!token;
    return conectado.value;
  };

  return {
    calendarHabilitado,
    conectado,
    cargando,
    error,
    eventosPendientes,
    conectarGoogleCalendar,
    desconectarGoogleCalendar,
    reconectarSilencioso,
    exportarTurnoACalendar,
    exportarTodosLosTurnos,
    actualizarEventoEnCalendar,
    sincronizarDesdeCalendar,
    confirmarImportacion,
    rechazarImportacion,
    verificarConexion,
  };
}
